'use client';

import React from 'react';

interface UseCase {
  title: string;
  description: string;
  features: string[];
  icon: string;
  gradient: string;
}

const UseCasesSection = () => {
  const useCases: UseCase[] = [
  {
    title: 'Customer Support',
    description: 'Handle support calls instantly with AI that understands context and resolves issues efficiently.',
    features: [
    'Answer FAQs automatically',
    'Route complex issues to humans',
    'Provide 24/7 availability',
    'Multi-language support'],

    icon: '🎧',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    title: 'Appointment Scheduling',
    description: 'Never miss a booking. Let AI manage your calendar and schedule appointments seamlessly.',
    features: [
    'Real-time calendar sync',
    'Automated confirmations',
    'Reschedule handling',
    'SMS/Email reminders'],

    icon: '📅',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    title: 'Lead Qualification',
    description: 'Qualify leads intelligently and route hot prospects to your sales team instantly.',
    features: [
    'Smart lead scoring',
    'CRM integration',
    'Instant notifications',
    'Follow-up automation'],

    icon: '🎯',
    gradient: 'from-orange-500 to-red-500'
  },
  {
    title: 'Order Processing',
    description: 'Process orders, track shipments, and handle inquiries without human intervention.',
    features: [
    'Order status updates',
    'Payment processing',
    'Inventory checks',
    'Return handling'],

    icon: '🛒',
    gradient: 'from-green-500 to-teal-500'
  },
  {
    title: 'Virtual Receptionist',
    description: 'Professional call handling that greets callers and directs them to the right department.',
    features: [
    'Call routing',
    'Message taking',
    'Directory assistance',
    'After-hours coverage'],

    icon: '📞',
    gradient: 'from-indigo-500 to-purple-500'
  },
  {
    title: 'Survey & Feedback',
    description: 'Collect customer feedback and conduct surveys through natural voice conversations.',
    features: [
    'Automated surveys',
    'Sentiment analysis',
    'Real-time insights',
    'Follow-up actions'],

    icon: '📊',
    gradient: 'from-yellow-500 to-orange-500'
  }];


  return (
    <section data-component-id="src/app/homepage/components/UseCasesSection.tsx:90:4" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="90" data-component-end-line="148" data-component-file="UseCasesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20px-4%20sm%3Apx-6%20lg%3Apx-8%20bg-white%22%7D" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:91:6" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="91" data-component-end-line="147" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:93:8" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="93" data-component-end-line="97" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-16%22%7D" className="text-center mb-16">
          <h2 data-component-id="src/app/homepage/components/UseCasesSection.tsx:94:10" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="94" data-component-end-line="96" data-component-file="UseCasesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20sm%3Atext-5xl%20font-bold%20text-gray-900%20mb-4%22%2C%22textContent%22%3A%22Endless%20Possibilities%22%7D" className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Endless Possibilities
          </h2>
        </div>

        {/* Use Cases Grid */}
        <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:100:8" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="100" data-component-end-line="136" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) =>
          <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:102:10" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="102" data-component-end-line="134" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22group%20relative%20bg-white%20rounded-2xl%20p-8%20shadow-lg%20hover%3Ashadow-2xl%20transition-all%20duration-300%20border%20border-gray-100%20hover%3Aborder-transparent%20overflow-hidden%22%7D"
            key={index}
            className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-transparent overflow-hidden">

              {/* Gradient Background on Hover */}
              <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:107:14" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="107" data-component-end-line="107" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

              {/* Content */}
              <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:110:14" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="110" data-component-end-line="133" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%22%7D" className="relative z-10">
                {/* Icon */}
                <div data-component-id="src/app/homepage/components/UseCasesSection.tsx:112:16" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="112" data-component-end-line="112" data-component-file="UseCasesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-5xl%20mb-4%22%7D" className="text-5xl mb-4">{useCase.icon}</div>

                {/* Title */}
                <h3 data-component-id="src/app/homepage/components/UseCasesSection.tsx:115:16" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="115" data-component-end-line="117" data-component-file="UseCasesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-gray-900%20mb-3%22%7D" className="text-2xl font-bold text-gray-900 mb-3">
                  {useCase.title}
                </h3>

                {/* Description */}
                <p data-component-id="src/app/homepage/components/UseCasesSection.tsx:120:16" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="120" data-component-end-line="122" data-component-file="UseCasesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20mb-6%20leading-relaxed%22%7D" className="text-gray-600 mb-6 leading-relaxed">
                  {useCase.description}
                </p>

                {/* Features List */}
                <ul data-component-id="src/app/homepage/components/UseCasesSection.tsx:125:16" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="125" data-component-end-line="132" data-component-file="UseCasesSection.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
                  {useCase.features.map((feature, idx) =>
                <li data-component-id="src/app/homepage/components/UseCasesSection.tsx:127:16" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="127" data-component-end-line="130" data-component-file="UseCasesSection.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-start%22%7D" key={idx} className="flex items-start">
                      <span data-component-id="src/app/homepage/components/UseCasesSection.tsx:128:22" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="128" data-component-end-line="128" data-component-file="UseCasesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-green-500%20mr-2%20mt-1%22%2C%22textContent%22%3A%22%E2%9C%93%22%7D" className="text-green-500 mr-2 mt-1">✓</span>
                      <span data-component-id="src/app/homepage/components/UseCasesSection.tsx:129:22" data-component-path="src/app/homepage/components/UseCasesSection.tsx" data-component-line="129" data-component-end-line="129" data-component-file="UseCasesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gray-700%22%7D" className="text-gray-700">{feature}</span>
                    </li>
                )}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        







      </div>
    </section>);

};

export default UseCasesSection;